import create from 'zustand';
import { GetState, SetState, StateSelector, EqualityChecker } from 'types/zustand';
import { useCallback, useEffect } from 'react';
import { getTranslatedTexts } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Translation/TranslationService';

export interface ITranslationSelector<U> {
  (selector: StateSelector<TranslationState, U>, equalityFn?: EqualityChecker<U>): U;
}

//#region store creation

const defaultTranslationState = {
  locale: 'en-us',
  // consider map of maps by first letter if we hit performance bottle neck with one flat map
  phrases: new Map<string, string>(),
};

type TranslationStoreActions = {
  actions: ReturnType<typeof createTranslationActions>;
};

type TranslationState = typeof defaultTranslationState & TranslationStoreActions;

function createTranslationActions(get: GetState<TranslationState>, set: SetState<TranslationState>) {
  return {
    /**
     * We could fetch a bunch of stuff at once using this, but for now we'll just assign the locale
     * @param locale to set
     */
    setLocale: async (locale: string) => {
      set({ locale });
    },

    /**
     * This will load and save to the data store any phrases which are missing in the list
     * @param phrases to ensure are load
     */
    loadPhrases: async (phrases: string[]) => {
      const { phrases: currentPhrases } = get();
      let missingPhrases: string[] = [];

      phrases.forEach((text: string) => {
        if (!currentPhrases.has(text)) {
          missingPhrases.push(text);
        }
      });

      if (missingPhrases && missingPhrases.length) {
        const translatedTexts = await getTranslatedTexts({ texts: missingPhrases });
        const updated = new Map(currentPhrases); // copy existing and call `set` only once per cluster
        translatedTexts.forEach(dp => updated.set(dp.Name, dp.OutputValue));
        set({ phrases: updated });
      }
    },
  };
}

/**
 * This is maybe not as ideal as a more complete solution like i18n-next, but it'll get us started in a way that
 * should provide an app-wide configuration we can scale up if we need to, or replace in one spot/way, and it
 * uses a library which we already have permission to use.
 *
 * Because it's a zustand store, and components subscribe to changes reactively, we could also do slick things
 * like update the language without refreshing the page, powered by something like this.
 */
export const useTranslationStore = create<TranslationState>((set, get) => ({
  ...defaultTranslationState,
  actions: createTranslationActions(get, set),
}));

//#endregion

//#region selector functions

// selector functions could just as easily be inline, but having them pre-defined can DRY up
// reuse, and make them easier to test if they had a complex calculation.
// They also shield components from the effects of store-object refactors.

export function getTranslationActions(state: TranslationState) {
  return state.actions;
}

//#endregion

//#region hooks
// hooks help DRY up boilerplate around getting certain references, e.g.

/**
 * Get a specific phrase in the current, if it's loaded. If it's not loaded it will return the original,
 * and the hook will fire again when the phrase is loaded.
 *
 * We could easily make this also load the phrase if it's missing, but for now it's probably better for
 * performance to force devs to think about what phrases need to be loaded for a particular area so they
 * can be loaded in batches.
 *
 * @param phrase some local state you need to use with shared state to pick / derive a value
 * @param translate pass `false` if you need to conditionally skip translation for some reason.
 * @returns the translated phrase, or if it's not loaded, the original phrase.
 */
export function useTranslatedPhrase(phrase: string, translate = true) {
  return useTranslationStore(
    useCallback(state => (translate ? state.phrases.get(phrase) || phrase : phrase), [phrase, translate])
  );
}

/**
 * This will not fetch the same phrases over and over, but will loop through them to check if you pass
 * a different array object in each time.
 *
 * @param phrases to ensure are loaded
 */
export function useEnsureTranslationsLoaded(phrases: string[]) {
  const {
    actions: { loadPhrases },
  } = useTranslationStore();
  useEffect(() => {
    loadPhrases(phrases);
  }, [loadPhrases, phrases]);
}

//#endregion hooks
